/**
 * reportClientError — best-effort POST of a structured client-error event to
 * apps/api. Used by `system`-classified mutate failures so the operator can
 * see real edge cases via Workers Logs.
 *
 * Strict invariants:
 *
 *   1. **Best-effort.** The reporter NEVER throws and NEVER blocks the caller.
 *      A failing report is silent — we cannot log a logging failure without
 *      looping, and the user has already seen the inline error from the caller.
 *
 *   2. **Privacy.** No user-input strings (recipe titles, body content, names,
 *      emails, handles) are ever included. The `context` parameter is for
 *      structural IDs only (uuids, mode keys, status codes). The truncation
 *      caps below cap the absolute risk if a misuse slips through.
 *
 *   3. **Server-stamped userUuid.** The endpoint reads userUuid from the
 *      authenticated session — the client never declares user identity.
 *
 *   4. **No new dependencies.** Plain `globalThis.fetch`. Same `credentials:
 *      "include"` + `X-CSRF-Token` shape used by the rest of the API surface.
 *
 * Plan refs:
 *   .plan/features/2026-05-04-error-handling-and-observability.md §Design,
 *   §"Privacy invariants", §"Failure-of-failure handling".
 */

import { getCsrfHeader } from "../auth/csrf.js";
import type { ClassifiedError } from "./classify.js";

// ── Caps ──────────────────────────────────────────────────────────────────────

/** Per-message cap on the wire (chars). */
const ERROR_MESSAGE_MAX_CHARS = 500;
/** Per-stack cap on the wire (chars). */
const ERROR_STACK_MAX_CHARS = 4000;
/** Reasonable cap on the user-agent string. */
const UA_MAX_CHARS = 256;
/** Locale strings are short — cap defensively anyway. */
const LOCALE_MAX_CHARS = 64;

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Device attributes captured once on hook-mount (caller passes them in).
 * Keeping this as an explicit shape rather than `Record<string, unknown>`
 * makes it impossible to accidentally smuggle a user-input string in via
 * a typo (e.g. `recipeTitle` would not type-check).
 */
export interface DeviceAttrs {
  userAgent: string;
  viewportPx: { w: number; h: number };
  locale: string;
  online: boolean;
  /** True when running in PWA standalone mode. */
  pwa: boolean;
  /** Unix-minute offset; integer matches `Date.prototype.getTimezoneOffset`. */
  timezoneOffset: number;
}

/**
 * Caller-supplied structural context. Typed as a record of primitives so the
 * site discourages user-input strings; consumers are expected to pass
 * IDs only — `{ recipeUuid, mode }`, never `{ recipeTitle }`.
 */
export type ReportContext = Record<string, string | number | boolean | null | undefined>;

export interface ReportClientErrorPayload {
  eventName: string;
  errorCode: string;
  errorMessage: string;
  errorStack: string | null;
  device: DeviceAttrs;
  context: ReportContext;
  timestamp: number;
}

export interface ReportClientErrorOptions {
  /** API origin override; defaults to "" (same-origin via the router Worker). */
  baseUrl?: string;
  /** Override fetch — used by tests. */
  fetch?: typeof fetch;
  /** Override device attrs — caller usually captures once on mount. */
  device: DeviceAttrs;
}

// ── Truncation helpers ────────────────────────────────────────────────────────

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max);
}

function readErrorMessage(err: unknown): string {
  if (err instanceof Error) return truncate(err.message, ERROR_MESSAGE_MAX_CHARS);
  if (typeof err === "string") return truncate(err, ERROR_MESSAGE_MAX_CHARS);
  try {
    return truncate(JSON.stringify(err) ?? String(err), ERROR_MESSAGE_MAX_CHARS);
  } catch {
    return truncate(String(err), ERROR_MESSAGE_MAX_CHARS);
  }
}

function readErrorStack(err: unknown): string | null {
  if (err instanceof Error && typeof err.stack === "string") {
    return truncate(err.stack, ERROR_STACK_MAX_CHARS);
  }
  return null;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Build the payload that will be sent to apps/api. Pure — no I/O.
 *
 * Exported so tests + the React hook can build the payload without a fetch
 * call, and so the privacy assertion in tests can inspect the object shape
 * directly.
 */
export function buildClientErrorPayload(
  eventName: string,
  classified: ClassifiedError,
  err: unknown,
  context: ReportContext,
  device: DeviceAttrs,
): ReportClientErrorPayload {
  return {
    eventName,
    errorCode: classified.code,
    errorMessage: readErrorMessage(err),
    errorStack: readErrorStack(err),
    device: {
      userAgent: truncate(device.userAgent, UA_MAX_CHARS),
      viewportPx: {
        w: Number.isFinite(device.viewportPx.w) ? device.viewportPx.w : 0,
        h: Number.isFinite(device.viewportPx.h) ? device.viewportPx.h : 0,
      },
      locale: truncate(device.locale, LOCALE_MAX_CHARS),
      online: !!device.online,
      pwa: !!device.pwa,
      timezoneOffset: Number.isFinite(device.timezoneOffset) ? device.timezoneOffset : 0,
    },
    context,
    timestamp: Date.now(),
  };
}

/**
 * POST a client-error event to apps/api. Best-effort: never throws.
 *
 * Resolves to `true` on a 2xx response, `false` on any other outcome
 * (non-2xx, network failure, parse error). The boolean is exposed for tests;
 * production callers can ignore it.
 */
export async function reportClientError(
  eventName: string,
  classified: ClassifiedError,
  err: unknown,
  context: ReportContext,
  options: ReportClientErrorOptions,
): Promise<boolean> {
  // Early-out: never report user errors. The classifier already decided this
  // path; the guard here is defensive in case a caller ignores the `kind`.
  if (classified.kind === "user") return false;

  const baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
  const f = options.fetch ?? globalThis.fetch;
  if (typeof f !== "function") return false;

  const payload = buildClientErrorPayload(eventName, classified, err, context, options.device);

  // The endpoint mounts at /rpc/clientError/report (oRPC) — see
  // apps/api/src/routes/client-error.ts. oRPC's HTTP transport accepts a JSON
  // body shaped as { json: <input>, meta: [] }.
  const url = `${baseUrl}/rpc/clientError/report`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const csrf = getCsrfHeader();
  if (csrf) headers["X-CSRF-Token"] = csrf;

  // Best-effort: do NOT keep the page hanging on a slow report.
  // 5-second AbortSignal so a hung server can't block teardown. Built
  // conditionally so we don't materialise an `undefined` field under
  // `exactOptionalPropertyTypes: true`.
  const init: RequestInit = {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ json: payload, meta: [] }),
  };
  if (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal) {
    init.signal = AbortSignal.timeout(5_000);
  }

  try {
    const res = await f(url, init);
    return res.ok;
  } catch {
    // Swallowed deliberately — see file header §1 + plan §"Failure-of-failure".
    return false;
  }
}
